import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useUserDataContext } from "../../user_data_context";
import { clearPendingQuestion } from "../utils/pending_question";
import AuthModal from "./auth_modal";
import type { AuthReason } from "./auth_card";
import {
	initialFlow,
	rememberOnClose,
	resumeFlow,
	type AuthFlow,
	type AuthScreen,
	type SavedFlow,
} from "./auth_flow";
import { clearResume, rememberResume, type ResumeMarker } from "./auth_resume";

export type OpenAuthOptions = {
	screen: AuthScreen;
	reason?: AuthReason;
	/** Where Google brings the reader back; the current page by default. */
	returnTo?: string;
	appearance?: "site" | "light";
	/** What to do once signed in without leaving the page (password or code). */
	onSignedIn?: () => void;
	/** What to continue after a Google round trip. */
	resume?: ResumeMarker;
};

type Opened = Required<Pick<OpenAuthOptions, "reason" | "returnTo" | "appearance">> &
	Pick<OpenAuthOptions, "onSignedIn" | "resume"> & { flow: AuthFlow; key: number };

const AuthModalContext = createContext<{ openAuth: (o: OpenAuthOptions) => void } | null>(null);

export const AuthModalProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const { fetchUserData } = useUserDataContext();
	const [opened, setOpened] = useState<Opened | null>(null);
	// A reader closed while waiting for a code: the next opening brings that screen back.
	const savedRef = useRef<SavedFlow | null>(null);
	const here = location.pathname + location.search;

	const openAuth = useCallback(
		({
			screen,
			reason = "default",
			returnTo,
			appearance = "site",
			onSignedIn,
			resume,
		}: OpenAuthOptions) => {
			const flow = resumeFlow(savedRef.current, Date.now()) ?? initialFlow(screen);
			savedRef.current = null;
			setOpened({
				flow,
				reason,
				returnTo: returnTo ?? here,
				appearance,
				onSignedIn,
				resume,
				key: Date.now(),
			});
		},
		[here],
	);

	const onClose = useCallback((flow: AuthFlow | null) => {
		const saved = rememberOnClose(flow, Date.now());
		// Closed before signing up began: nothing waits for this sign-in any more.
		if (flow && !saved) {
			clearPendingQuestion();
			clearResume();
		}
		savedRef.current = saved;
		setOpened(null);
	}, []);

	const signedIn = useCallback(
		(token: string) => {
			localStorage.setItem("access_token", token);
			fetchUserData();
			// Continued right here: a marker from a right-click or long-press on Google must not run it twice.
			if (opened?.resume) clearResume();
			const after = opened?.onSignedIn;
			setOpened(null);
			after?.();
		},
		[fetchUserData, opened],
	);

	const value = useMemo(() => ({ openAuth }), [openAuth]);

	return (
		<AuthModalContext.Provider value={value}>
			{children}
			{opened && (
				<AuthModal
					key={opened.key}
					initial={opened.flow}
					reason={opened.reason}
					returnTo={opened.returnTo}
					appearance={opened.appearance}
					onSignedIn={signedIn}
					onLeaveForGoogle={opened.resume ? () => rememberResume(opened.resume!) : undefined}
					onClose={onClose}
				/>
			)}
		</AuthModalContext.Provider>
	);
};

export const useAuthModal = () => {
	const ctx = useContext(AuthModalContext);
	if (!ctx) throw new Error("useAuthModal must be used inside AuthModalProvider");
	return ctx;
};
